import Highcharts from 'highcharts';

// Types
import Locale from '@/types/locale';
import HistoricalScenario from '@/types/historical-scenario';
import { ScenarioMetrics } from '@/types/calculated-historical-scenario';

// Utils
import { currencyToText } from '@/utils/currency';
import { PERFORMANCE_CHART } from '@/utils/constants';
import { formatLocalizedString, formatMessage, formatDateWithLocale, formatPercentage, formatNumber } from '@/utils/i18n';
import { HighchartPoint } from '@/views/components/charts/utils/types/tooltip';
import { getGrowthChartTooltipBody, getIconName } from '@/views/components/charts/growth-chart/utils/tooltip';
import { getChartTooltipHeader, getCommonTooltipBody, getTooltipPointsToDisplay, MAX_ITEMS_IN_CHART_TOOLTIP } from '@/views/components/charts/utils/tooltip';

export const MAX_ITEMS_WITH_SCENARIO_IN_TOOLTIP = 7;

const getCurrentScenario = (availableScenarios: HistoricalScenario[], x: number) =>
  availableScenarios.find((s: HistoricalScenario) => {
    const start = new Date(s.startDate).getTime();
    const end = new Date(s.endDate).getTime();
    return x >= start && x <= end;
  });

const getScenarioTemplate = (locale: Locale, scenario?: HistoricalScenario) => {
  if (scenario) {
    return `<div class="chart-tooltip-scenario">
      <div class="chart-tooltip-scenario-section-title pill-label pill-label-v2 purple">${formatMessage('Comparison.HistoricalScenarios.ChartTooltipHistoricalEvent')}</div>
      <div class="chart-tooltip-scenario-name">${formatLocalizedString(scenario.name)}</div>
      <div class="chart-tooltip-scenario-dates">
        <span class="chart-tooltip-scenario-dates-label">${formatMessage('Comparison.HistoricalScenarios.ChartTooltipScenarioDate')}</span>
        <span>${formatDateWithLocale(new Date(scenario.startDate), locale, 'MMM yyyy')} - ${formatDateWithLocale(new Date(scenario.endDate), locale, 'MMM yyyy')}</span>
      </div>
      <div class="chart-tooltip-scenario-description">${formatLocalizedString(scenario.shortDescription)}</div>
    </div>`;
  }
  return '';
};

const getMetricsFromSerie = (point: HighchartPoint, currentScenario?: HistoricalScenario): ScenarioMetrics | null => {
  if (!currentScenario || !point) return null;

  const scenarioMetrics = (point.series?.options as any)?.scenarioMetrics;
  if (!scenarioMetrics?.scenarios) return null;
  return scenarioMetrics.scenarios.find((scenario: any) => scenario.historicalScenario._id === currentScenario._id);
};

const getMetricsTemplate = (point: HighchartPoint, currentScenario: HistoricalScenario) => {
  if (!point) return '';
  const metrics = getMetricsFromSerie(point, currentScenario);
  return `<tr class="chart-tooltip-item">
    <td class="chart-tooltip-item-name pe-4" data-private data-dd-privacy="mask">
      <i class="${getIconName(point.series.userOptions.itemType)} holding-icon" style="color: ${point.series.color};"></i>
      ${point.series.name}
    </td>
    <td class="chart-tooltip-item-return pe-4 ${Number(metrics?.annualizedReturns) < 0 ? 'negative-value' : ''}" data-private data-dd-privacy="mask">${formatPercentage(Number(metrics?.annualizedReturns), 2)}</td>
    <td class="chart-tooltip-item-standard-deviation pe-4" data-private data-dd-privacy="mask">${formatNumber(Number(metrics?.standardDeviation), 2)}</td>
    <td class="chart-tooltip-item-value text-end">${Number(point.y).toFixed(2)} ${currencyToText(point.series.userOptions.currency)}</td>
  </tr>`;
};

export function getScenarioChartTooltipBody(points: HighchartPoint[], currentScenario: HistoricalScenario, maxItems = MAX_ITEMS_IN_CHART_TOOLTIP) {
  const body = `<table class="chart-tooltip-table-scenario">
      <thead>
        <tr>
          <th>${formatMessage('Comparison.HistoricalScenarios.ChartTooltipTable.Investment')}</th>
          <th>${formatMessage('Comparison.HistoricalScenarios.ChartTooltipTable.AnnualizedReturn')}</th>
          <th>${formatMessage('Comparison.HistoricalScenarios.ChartTooltipTable.StandardDeviation')}</th>
          <th>${formatMessage('Comparison.HistoricalScenarios.ChartTooltipTable.Value')}</th>
        </tr>
      </thead>${getTooltipPointsToDisplay(points, maxItems)
        .map(point => getMetricsTemplate(point, currentScenario))
        .join('')}</table>`;
  const overflowNotice =
    points.length > maxItems ? `<div class="chart-tooltip-overflow-notice">+ ${points.length - maxItems} ${formatMessage('Common.More')}</div>` : '';
  return `<div class="chart-tooltip-body">${body}${overflowNotice}</div>`;
}

export const makeHistoricalScenarioTooltipFormatter = (locale: Locale, selectedScenarios: HistoricalScenario[], selectedChart?: PERFORMANCE_CHART) =>
  function (this: Highcharts.TooltipFormatterContextObject & { points: HighchartPoint[] }) {
    const isDrawdownChart = selectedChart && selectedChart === PERFORMANCE_CHART.DRAWDOWN;
    const currentScenario = getCurrentScenario(selectedScenarios, this.x as number);
    const maxItems = currentScenario ? MAX_ITEMS_WITH_SCENARIO_IN_TOOLTIP : MAX_ITEMS_IN_CHART_TOOLTIP;
    const date = getChartTooltipHeader({ x: this.x as number, timeFormatter: this.points?.[0].series?.chart?.time });
    const scenarioInfo = getScenarioTemplate(locale, currentScenario);
    const body = isDrawdownChart ? getCommonTooltipBody(this.points, maxItems) : getGrowthChartTooltipBody(this.points, maxItems);
    if (currentScenario && selectedChart === PERFORMANCE_CHART.GROWTH) {
      const metricsInfo = getScenarioChartTooltipBody(this.points, currentScenario, maxItems);
      return date + scenarioInfo + metricsInfo;
    }
    return date + scenarioInfo + body;
  };
